import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

applyPlugin(jsPDF);
import * as XLSX from 'xlsx';
import {
  getFinancialReportSummary,
  getProfitDetails,
  getExpenses,
  getPayments,
} from '@/lib/supabaseDb';
import { getEmployees } from '@/lib/employeeDb';
import type { FinancialReportSummary, ProfitDetailItem } from '@/lib/supabaseDb';
import { formatTRY } from '@/lib/currencyFormat';
import { toTurkishLower } from '@/lib/customerSearch';

// ── Istanbul date helpers ──
function trNow(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
}
function trToday(): string {
  return trNow().toLocaleDateString('en-CA');
}
function startOfWeek(d: Date): string {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d);
  mon.setDate(diff);
  return mon.toLocaleDateString('en-CA');
}
function startOfMonth(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function startOfYear(d: Date): string {
  return `${d.getFullYear()}-01-01`;
}
function endOfMonth(d: Date): string {
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return lastDay.toLocaleDateString('en-CA');
}
function subtractMonths(date: Date, n: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() - n);
  return d;
}

type FilterKey = 'today' | 'week' | 'month' | 'lastMonth' | 'year' | 'custom';

type DetailCardType = 'revenue' | 'gross_profit' | 'net_profit' | 'collection' | 'sales_count' | 'total_cost' | 'total_expenses' | 'known_profit' | 'unknown_cost' | null;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'today', label: 'Bugün' },
  { key: 'week', label: 'Bu Hafta' },
  { key: 'month', label: 'Bu Ay' },
  { key: 'lastMonth', label: 'Geçen Ay' },
  { key: 'year', label: 'Bu Yıl' },
  { key: 'custom', label: 'Özel' },
];

function buildDateRange(filter: FilterKey): { start: string; end: string } {
  const now = trNow();
  switch (filter) {
    case 'today': { const t = trToday(); return { start: t, end: t }; }
    case 'week': return { start: startOfWeek(now), end: trToday() };
    case 'month': return { start: startOfMonth(now), end: trToday() };
    case 'lastMonth': {
      const prev = subtractMonths(now, 1);
      return { start: startOfMonth(prev), end: endOfMonth(prev) };
    }
    case 'year': return { start: startOfYear(now), end: trToday() };
    case 'custom':
    default: return { start: startOfMonth(now), end: trToday() };
  }
}

// ── Profit Details Modal ──
function ProfitDetailsModal({
  open,
  onClose,
  startDate,
  endDate,
}: {
  open: boolean;
  onClose: () => void;
  startDate: string;
  endDate: string;
}) {
  const [items, setItems] = useState<ProfitDetailItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);

  // Filters
  const [search, setSearch] = useState('');
  const [filterProfit, setFilterProfit] = useState<'all' | 'profitable' | 'loss' | 'unknown'>('all');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'known' | 'unknown'>('all');
  const [expandedSales, setExpandedSales] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    Promise.all([
      getProfitDetails(startDate, endDate),
      getEmployees(),
    ]).then(([detailRes, empRes]) => {
      if (detailRes.error) {
        setError(detailRes.error);
      } else if (detailRes.data) {
        setItems(detailRes.data.items || []);
      }
      setEmployees((empRes.data as any[]) || []);
    }).catch(() => setError('Veriler yüklenirken hata oluştu'))
      .finally(() => setLoading(false));
  }, [open, startDate, endDate]);

  // Group items by sale
  const saleGroups = (() => {
    const map = new Map<string, { saleNo: string; saleDate: string; customerName: string; saleTotal: number; employeeId: string | null; createdBy: string | null; items: ProfitDetailItem[] }>();
    items.forEach((item) => {
      const key = item.sale_id;
      if (!map.has(key)) {
        map.set(key, {
          saleNo: item.sale_no,
          saleDate: item.sale_date,
          customerName: item.customer_name,
          saleTotal: item.sale_total,
          employeeId: item.employee_id,
          createdBy: item.created_by,
          items: [],
        });
      }
      map.get(key)!.items.push(item);
    });
    return Array.from(map.values());
  })();

  // Apply tab filter first, then other filters
  const tabFilteredGroups = saleGroups.filter((g) => {
    const allUnknown = g.items.every(it => it.item_profit === null);
    if (activeTab === 'known') return !allUnknown;
    if (activeTab === 'unknown') return allUnknown;
    return true;
  });

  const filteredGroups = tabFilteredGroups.filter((g) => {
    if (search) {
      const s = toTurkishLower(search.trim());
      if (!toTurkishLower(g.saleNo).includes(s) && !toTurkishLower(g.customerName).includes(s) && !g.items.some(it => toTurkishLower(it.product_name || '').includes(s) || toTurkishLower(it.brand || '').includes(s) || toTurkishLower(it.model_code || '').includes(s))) return false;
    }
    if (filterEmployee) {
      if (g.employeeId !== filterEmployee && g.createdBy !== filterEmployee) return false;
    }
    if (filterProfit !== 'all') {
      const totalProfit = g.items.reduce((sum, it) => sum + (it.item_profit ?? 0), 0);
      const allUnknown = g.items.every(it => it.item_profit === null);
      if (filterProfit === 'profitable' && (allUnknown || totalProfit <= 0)) return false;
      if (filterProfit === 'loss' && !allUnknown && totalProfit >= 0) return false;
      if (filterProfit === 'unknown' && !allUnknown) return false;
    }
    return true;
  });

  const toggleExpand = (saleId: string) => {
    setExpandedSales((prev) => {
      const next = new Set(prev);
      if (next.has(saleId)) next.delete(saleId);
      else next.add(saleId);
      return next;
    });
  };

  const empMap = new Map(employees.map((e: any) => [e.id, e.full_name]));

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-background-50 rounded-2xl w-[95vw] max-w-5xl max-h-[90vh] flex flex-col shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-background-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-foreground-900 font-heading">Kâr Detayları</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-foreground-600"></i>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-background-100 flex-shrink-0">
          {/* Kâr sekmeleri */}
          <div className="flex items-center bg-background-100 rounded-full p-1 mr-2">
            {([
              { key: 'all', label: 'Tüm Satışlar' },
              { key: 'known', label: 'Kârı Hesaplanabilen' },
              { key: 'unknown', label: 'Maliyeti Eksik' },
            ] as const).map((tab) => (
              <button
                key={tab.key}
                onClick={(e) => { e.stopPropagation(); setActiveTab(tab.key); }}
                className={`text-xs px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'text-foreground-600 hover:text-foreground-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Satış no / Müşteri / Ürün ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-sm border border-background-200 rounded-lg px-3 py-2 w-56 bg-transparent text-foreground-800 placeholder:text-foreground-400"
          />
          <select
            value={filterProfit}
            onChange={(e) => setFilterProfit(e.target.value as any)}
            className="text-sm border border-background-200 rounded-lg px-3 py-2 bg-transparent text-foreground-800 cursor-pointer"
          >
            <option value="all">Tüm Satışlar</option>
            <option value="profitable">Kârlı Satışlar</option>
            <option value="loss">Zarar Eden</option>
            <option value="unknown">Maliyeti Eksik</option>
          </select>
          <select
            value={filterEmployee}
            onChange={(e) => setFilterEmployee(e.target.value)}
            className="text-sm border border-background-200 rounded-lg px-3 py-2 bg-transparent text-foreground-800 cursor-pointer"
          >
            <option value="">Tüm Personel</option>
            {employees.map((e: any) => (
              <option key={e.id} value={e.id}>{e.full_name}</option>
            ))}
          </select>
          <span className="text-xs text-foreground-400 ml-auto">{filteredGroups.length} satış</span>
        </div>

        {/* Tab summary - kartlarla aynı veri */}
        {activeTab !== 'all' && (
          <div className="px-4 py-2.5 bg-background-100/50 border-b border-background-100 flex flex-wrap items-center gap-4 text-xs flex-shrink-0">
            <span className="text-foreground-500">
              {activeTab === 'known' ? 'Kârı Hesaplanabilen' : 'Maliyeti Eksik'} satış sayısı:
              <strong className="text-foreground-800 ml-1">{filteredGroups.length}</strong>
            </span>
            {activeTab === 'known' && (() => {
              const totalProfit = filteredGroups.reduce((s, g) => {
                const tp = g.items.reduce((sum, it) => sum + (it.item_profit ?? 0), 0);
                const allUnknown = g.items.every(it => it.item_profit === null);
                return s + (allUnknown ? 0 : tp);
              }, 0);
              const totalRev = filteredGroups.reduce((s, g) => {
                const allUnknown = g.items.every(it => it.item_profit === null);
                return s + (allUnknown ? 0 : g.items.reduce((sum, it) => sum + (it.item_revenue || 0), 0));
              }, 0);
              return (
                <>
                  <span className="text-foreground-500">
                    Toplam Kâr:
                    <strong className={`ml-1 ${totalProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatTRY(totalProfit)}</strong>
                  </span>
                  <span className="text-foreground-400 text-[10px]">
                    (Üst karttaki Brüt Kâr ile aynı)
                  </span>
                </>
              );
            })()}
            {activeTab === 'unknown' && (() => {
              const totalUnknownRev = filteredGroups.reduce((s, g) => s + g.items.reduce((sum, it) => sum + (it.item_revenue || 0), 0), 0);
              return (
                <span className="text-foreground-500">
                  Toplam Ciro:
                  <strong className="text-foreground-800 ml-1">{formatTRY(totalUnknownRev)}</strong>
                </span>
              );
            })()}
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <p className="text-red-500 text-center py-10">{error}</p>
          ) : filteredGroups.length === 0 ? (
            <p className="text-foreground-400 text-center py-10">Veri bulunamadı</p>
          ) : (
            filteredGroups.map((g) => {
              const totalRevenue = g.items.reduce((s, it) => s + (it.item_revenue || 0), 0);
              const totalCost = g.items.reduce((s, it) => s + (it.item_cost ?? 0), 0);
              const totalProfit = g.items.reduce((s, it) => s + (it.item_profit ?? 0), 0);
              const allUnknown = g.items.every(it => it.item_profit === null);
              const profitRate = !allUnknown && totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100) : null;
              const empName = g.employeeId ? empMap.get(g.employeeId) || g.createdBy : g.createdBy;
              const isExpanded = expandedSales.has(g.saleNo + g.saleDate);

              return (
                <div key={g.saleNo + g.saleDate} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                  <div
                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-background-100/50 transition-colors"
                    onClick={() => toggleExpand(g.saleNo + g.saleDate)}
                  >
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                      <i className="ri-arrow-right-s-line text-foreground-500"></i>
                    </span>
                    <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-6 gap-2 items-center">
                      <span className="text-sm font-semibold text-primary-600 truncate">{g.saleNo}</span>
                      <span className="text-sm text-foreground-700 truncate">{g.customerName}</span>
                      <span className="text-sm font-medium text-foreground-800 text-right md:text-left">{formatTRY(totalRevenue)}</span>
                      <span className="text-sm text-foreground-500 text-right md:text-left">{allUnknown ? '—' : formatTRY(totalCost)}</span>
                      <span className={`text-sm font-semibold text-right md:text-left whitespace-nowrap ${allUnknown ? 'text-foreground-400' : totalProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {allUnknown ? 'Maliyet Eksik' : formatTRY(totalProfit)}
                        {profitRate !== null && <span className="text-xs ml-1">({profitRate >= 0 ? '+' : ''}%{Math.round(profitRate)})</span>}
                      </span>
                      <span className="text-xs text-foreground-400 text-right truncate hidden md:block">{empName || '—'}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-background-100 bg-background-100/30 px-4 py-3">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-background-200">
                              <th className="text-left py-2 text-foreground-500 font-medium">Ürün</th>
                              <th className="text-left py-2 text-foreground-500 font-medium">Model</th>
                              <th className="text-center py-2 text-foreground-500 font-medium">Adet</th>
                              <th className="text-right py-2 text-foreground-500 font-medium">Birim Maliyet</th>
                              <th className="text-right py-2 text-foreground-500 font-medium">Birim Satış</th>
                              <th className="text-right py-2 text-foreground-500 font-medium">Toplam Maliyet</th>
                              <th className="text-right py-2 text-foreground-500 font-medium">Toplam Satış</th>
                              <th className="text-right py-2 text-foreground-500 font-medium">Kâr</th>
                              <th className="text-right py-2 text-foreground-500 font-medium">Kâr %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.items.map((it) => (
                              <tr key={it.item_id} className="border-b border-background-100">
                                <td className="py-2 text-foreground-700 font-medium">{it.product_name || '—'}</td>
                                <td className="py-2 text-foreground-500">{it.model_code || '—'}</td>
                                <td className="py-2 text-center text-foreground-700">{it.quantity}</td>
                                <td className="py-2 text-right text-foreground-500">{it.unit_cost_price != null ? formatTRY(it.unit_cost_price) : '—'}</td>
                                <td className="py-2 text-right text-foreground-700">{formatTRY(it.unit_sale_price)}</td>
                                <td className="py-2 text-right text-foreground-500">{it.item_cost != null ? formatTRY(it.item_cost) : '—'}</td>
                                <td className="py-2 text-right text-foreground-700 font-medium">{formatTRY(it.item_revenue)}</td>
                                <td className={`py-2 text-right font-semibold whitespace-nowrap ${it.item_profit === null ? 'text-foreground-400' : it.item_profit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {it.item_profit === null ? 'Eksik' : formatTRY(it.item_profit)}
                                </td>
                                <td className={`py-2 text-right font-medium whitespace-nowrap ${it.profit_rate === null ? 'text-foreground-400' : it.profit_rate >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {it.profit_rate === null ? '—' : `%${it.profit_rate}`}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ── Card Detail Modal (tıklanan özet kartının detayını gösterir) ──
function CardDetailModal({
  open,
  onClose,
  cardType,
  summary,
  startDate,
  endDate,
}: {
  open: boolean;
  onClose: () => void;
  cardType: DetailCardType;
  summary: FinancialReportSummary;
  startDate: string;
  endDate: string;
}) {
  const [items, setItems] = useState<ProfitDetailItem[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !cardType) return;
    setLoading(true);
    setError(null);
    setItems([]);
    setExpenses([]);
    setPayments([]);

    const fetchers: Promise<any>[] = [];

    const needsProfit = ['gross_profit', 'net_profit', 'known_profit', 'unknown_cost', 'sales_count', 'revenue', 'total_cost'].includes(cardType);
    if (needsProfit) {
      fetchers.push(
        getProfitDetails(startDate, endDate).then((res) => {
          if (res.error) setError(res.error);
          else setItems(res.data?.items || []);
        })
      );
    }

    if (cardType === 'total_expenses') {
      fetchers.push(
        getExpenses().then((res) => {
          if (res.error) setError(res.error);
          else {
            const data = (res.data || []) as any[];
            const filtered = data.filter((e: any) => {
              const d = e.date || '';
              return d >= startDate && d <= endDate;
            });
            setExpenses(filtered);
          }
        })
      );
    }

    if (cardType === 'collection') {
      fetchers.push(
        getPayments().then((res) => {
          if (res.error) setError(res.error);
          else {
            const data = (res.data || []) as any[];
            const filtered = data.filter((p: any) => {
              const d = p.payment_date || '';
              return d >= startDate && d <= endDate;
            });
            setPayments(filtered);
          }
        })
      );
    }

    Promise.all(fetchers).finally(() => setLoading(false));
  }, [open, cardType, startDate, endDate]);

  if (!open || !cardType) return null;

  const titleMap: Record<string, string> = {
    revenue: 'Toplam Ciro Detayı',
    gross_profit: 'Brüt Kâr Detayı',
    net_profit: 'Net Kâr Detayı',
    collection: 'Tahsilat & Alacak Detayı',
    sales_count: 'Satış Listesi',
    total_cost: 'Satılan Ürün Maliyet Detayı',
    total_expenses: 'Gider Detayı',
    known_profit: 'Kesin Brüt Kâr Detayı',
    unknown_cost: 'Maliyeti Bilinmeyen Ciro Detayı',
  };

  const saleGroups = (() => {
    const map = new Map<string, { saleNo: string; saleDate: string; customerName: string; saleTotal: number; items: ProfitDetailItem[] }>();
    items.forEach((item) => {
      const key = item.sale_id;
      if (!map.has(key)) {
        map.set(key, {
          saleNo: item.sale_no,
          saleDate: item.sale_date,
          customerName: item.customer_name,
          saleTotal: item.sale_total,
          items: [],
        });
      }
      map.get(key)!.items.push(item);
    });
    return Array.from(map.values());
  })();

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }
    if (error) return <p className="text-red-500 text-center py-10">{error}</p>;

    switch (cardType) {
      case 'revenue': {
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-background-100 rounded-xl p-4">
                <p className="text-xs text-foreground-400">Satış Cirosu</p>
                <p className="text-lg font-bold text-foreground-900">{formatTRY(summary.sales_revenue)}</p>
              </div>
              <div className="bg-background-100 rounded-xl p-4">
                <p className="text-xs text-foreground-400">Bağımsız Senet / Eski Borç</p>
                <p className="text-lg font-bold text-foreground-900">{formatTRY(summary.independent_note_revenue)}</p>
              </div>
            </div>
            {saleGroups.length > 0 && (
              <div>
                <p className="text-xs text-foreground-400 mb-2">Dönem içindeki satışlar ({saleGroups.length} adet)</p>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {saleGroups.slice(0, 20).map((g) => (
                    <div key={g.saleNo + g.saleDate} className="flex items-center justify-between py-2 px-3 bg-background-50 rounded-lg border border-background-100 text-xs">
                      <span className="text-primary-600 font-medium">{g.saleNo}</span>
                      <span className="text-foreground-600 truncate mx-2">{g.customerName}</span>
                      <span className="text-foreground-700 font-medium whitespace-nowrap">{formatTRY(g.saleTotal)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'gross_profit':
      case 'net_profit':
      case 'known_profit': {
        const filtered = cardType === 'known_profit'
          ? saleGroups.filter((g) => g.items.every((it) => it.item_profit !== null))
          : cardType === 'gross_profit'
            ? saleGroups.filter((g) => g.items.some((it) => it.item_profit !== null))
            : saleGroups;
        if (filtered.length === 0) return <p className="text-foreground-400 text-center py-10">Gösterilecek veri bulunamadı</p>;
        const filteredTotalProfit = filtered.reduce((s, g) => {
          const tp = g.items.reduce((sum, it) => sum + (it.item_profit ?? 0), 0);
          return s + (g.items.every(it => it.item_profit === null) ? 0 : tp);
        }, 0);
        return (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            <div className="bg-background-100 rounded-xl p-3 mb-3 flex items-center justify-between">
              <span className="text-xs text-foreground-500">
                {cardType === 'known_profit' ? 'Sadece maliyeti bilinen satışlar' : 'Maliyeti bilinen satışlar'} ({filtered.length} adet)
              </span>
              <span className={`text-sm font-bold ${filteredTotalProfit >= 0 ? 'text-accent-600' : 'text-red-500'}`}>
                Toplam: {formatTRY(filteredTotalProfit)}
              </span>
            </div>
            {filtered.map((g) => {
              const totalRevenue = g.items.reduce((s, it) => s + (it.item_revenue || 0), 0);
              const totalCost = g.items.reduce((s, it) => s + (it.item_cost ?? 0), 0);
              const totalProfit = g.items.reduce((s, it) => s + (it.item_profit ?? 0), 0);
              const allUnknown = g.items.every((it) => it.item_profit === null);
              return (
                <div key={g.saleNo + g.saleDate} className="flex items-center justify-between py-2.5 px-4 bg-background-50 rounded-lg border border-background-100">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-primary-600">{g.saleNo}</span>
                    <span className="text-xs text-foreground-400 ml-2">{g.customerName}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-foreground-500">Ciro: {formatTRY(totalRevenue)}</span>
                    <span className="text-foreground-400">Maliyet: {allUnknown ? '—' : formatTRY(totalCost)}</span>
                    <span className={`font-semibold whitespace-nowrap ${allUnknown ? 'text-foreground-400' : totalProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {allUnknown ? 'Eksik' : formatTRY(totalProfit)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      case 'unknown_cost': {
        const unknownGroups = saleGroups.filter((g) => g.items.some((it) => it.item_profit === null));
        if (unknownGroups.length === 0) return <p className="text-foreground-400 text-center py-10">Tüm maliyetler biliniyor, maliyeti bilinmeyen ciro yok.</p>;
        return (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {unknownGroups.map((g) => {
              const totalRevenue = g.items.reduce((s, it) => s + (it.item_revenue || 0), 0);
              const unknownItems = g.items.filter((it) => it.item_profit === null);
              return (
                <div key={g.saleNo + g.saleDate} className="py-2.5 px-4 bg-background-50 rounded-lg border border-background-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-primary-600">{g.saleNo}</span>
                      <span className="text-xs text-foreground-400 ml-2">{g.customerName}</span>
                    </div>
                    <span className="text-sm font-medium text-foreground-700">{formatTRY(totalRevenue)}</span>
                  </div>
                  <div className="mt-1.5 text-xs text-amber-600">
                    {unknownItems.map((it) => (
                      <span key={it.item_id} className="inline-block mr-3">{it.product_name || 'Ürün'} (maliyet eksik)</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      case 'collection': {
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 rounded-xl p-4">
                <p className="text-xs text-emerald-600">Tahsil Edilen</p>
                <p className="text-xl font-bold text-emerald-700">{formatTRY(summary.collected_amount)}</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="text-xs text-amber-600">Bekleyen Alacak</p>
                <p className="text-xl font-bold text-amber-700">{formatTRY(summary.outstanding_amount)}</p>
              </div>
            </div>
            {payments.length > 0 && (
              <div>
                <p className="text-xs text-foreground-400 mb-2">Dönem içinde yapılan tahsilatlar ({payments.length} adet)</p>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {payments.slice(0, 30).map((p: any, i: number) => (
                    <div key={p.id || i} className="flex items-center justify-between py-2 px-3 bg-background-50 rounded-lg border border-background-100 text-xs">
                      <span className="text-foreground-500 w-24 flex-shrink-0">{p.payment_date || '—'}</span>
                      <span className="text-foreground-600 truncate flex-1 mx-2">{p.customer_name || '—'}</span>
                      <span className="text-foreground-400 mr-2 flex-shrink-0">{p.payment_method || '—'}</span>
                      <span className="text-emerald-600 font-semibold whitespace-nowrap">{formatTRY(p.amount || 0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {payments.length === 0 && !loading && (
              <p className="text-foreground-400 text-center py-6">Bu dönemde tahsilat kaydı bulunamadı</p>
            )}
          </div>
        );
      }

      case 'sales_count': {
        const uniqueSales = new Map<string, { saleNo: string; customerName: string; saleDate: string; total: number }>();
        saleGroups.forEach((g) => {
          if (!uniqueSales.has(g.saleNo)) {
            uniqueSales.set(g.saleNo, { saleNo: g.saleNo, customerName: g.customerName, saleDate: g.saleDate, total: g.saleTotal });
          }
        });
        const salesList = Array.from(uniqueSales.values());
        if (salesList.length === 0) return <p className="text-foreground-400 text-center py-10">Bu dönemde satış bulunamadı</p>;
        return (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            <p className="text-xs text-foreground-400 mb-2">Toplam {summary.sales_count} satış</p>
            {salesList.slice(0, 50).map((s) => (
              <div key={s.saleNo} className="flex items-center justify-between py-2.5 px-4 bg-background-50 rounded-lg border border-background-100">
                <div>
                  <span className="text-sm font-semibold text-primary-600">{s.saleNo}</span>
                  <span className="text-xs text-foreground-400 ml-2">{s.customerName}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-foreground-400">{s.saleDate}</span>
                  <span className="text-sm font-medium text-foreground-700">{formatTRY(s.total)}</span>
                </div>
              </div>
            ))}
          </div>
        );
      }

      case 'total_cost': {
        const withCost = saleGroups.filter((g) => g.items.some((it) => it.item_cost !== null));
        if (withCost.length === 0) return <p className="text-foreground-400 text-center py-10">Maliyet bilgisi olan satış bulunamadı</p>;
        return (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            <div className="bg-background-100 rounded-xl p-4 mb-3">
              <p className="text-xs text-foreground-400">Toplam Satılan Ürün Maliyeti</p>
              <p className="text-xl font-bold text-red-500">{formatTRY(summary.total_cost)}</p>
            </div>
            {withCost.map((g) => {
              const totalCost = g.items.reduce((s, it) => s + (it.item_cost ?? 0), 0);
              const totalRevenue = g.items.reduce((s, it) => s + (it.item_revenue || 0), 0);
              const costRatio = totalRevenue > 0 ? ((totalCost / totalRevenue) * 100) : 0;
              return (
                <div key={g.saleNo + g.saleDate} className="flex items-center justify-between py-2.5 px-4 bg-background-50 rounded-lg border border-background-100">
                  <div>
                    <span className="text-sm font-semibold text-primary-600">{g.saleNo}</span>
                    <span className="text-xs text-foreground-400 ml-2">{g.customerName}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-foreground-400">Ciro: {formatTRY(totalRevenue)}</span>
                    <span className="text-red-500 font-medium">Maliyet: {formatTRY(totalCost)}</span>
                    <span className="text-foreground-400">(%{Math.round(costRatio)})</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      case 'total_expenses': {
        if (expenses.length === 0) return <p className="text-foreground-400 text-center py-10">Bu dönemde gider kaydı bulunamadı</p>;
        return (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            <div className="bg-red-50 rounded-xl p-4 mb-3">
              <p className="text-xs text-red-500">Toplam Gider</p>
              <p className="text-xl font-bold text-red-600">{formatTRY(summary.total_expenses)}</p>
            </div>
            {expenses.map((e: any, i: number) => (
              <div key={e.id || i} className="flex items-center justify-between py-2.5 px-4 bg-background-50 rounded-lg border border-background-100">
                <div>
                  <span className="text-sm text-foreground-700">{e.description || e.category || 'Gider'}</span>
                  <span className="text-xs text-foreground-400 ml-2">{e.date || '—'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-400">{e.category || ''}</span>
                  <span className="text-sm font-semibold text-red-500 whitespace-nowrap">{formatTRY(e.amount || 0)}</span>
                </div>
              </div>
            ))}
          </div>
        );
      }

      default:
        return <p className="text-foreground-400 text-center py-10">Detay bulunamadı</p>;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-background-50 rounded-2xl w-[95vw] max-w-3xl max-h-[85vh] flex flex-col shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-background-200 flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-foreground-900 font-heading">{titleMap[cardType] || 'Detay'}</h3>
            <p className="text-xs text-foreground-400 mt-0.5">{startDate} — {endDate}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-foreground-600"></i>
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

// ── Main Reports Page ──
export default function ReportsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const srcParam = searchParams.get('src');
  const periodParam = searchParams.get('period');
  const typeParam = searchParams.get('type');
  const [filterKey, setFilterKey] = useState<FilterKey>(
    periodParam === 'today' ? 'today' :
    periodParam === 'this_month' ? 'month' : 'month'
  );
  const [customStart, setCustomStart] = useState(trToday());
  const [customEnd, setCustomEnd] = useState(trToday());
  const [summary, setSummary] = useState<FinancialReportSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [showProfitModal, setShowProfitModal] = useState(false);
  const [detailCardType, setDetailCardType] = useState<DetailCardType>(null);
  // Auto-open profit details when coming from dashboard net profit card
  useEffect(() => {
    if (srcParam === 'dashboard' && typeParam === 'net_profit' && !loading && summary) {
      setShowProfitModal(true);
    }
  }, [srcParam, typeParam, loading, summary]);
  // Auto-open revenue details when coming from dashboard today-revenue card
  useEffect(() => {
    if (srcParam === 'dashboard' && typeParam === 'revenue' && !loading && summary) {
      setDetailCardType('revenue');
    }
  }, [srcParam, typeParam, loading, summary]);

  const dateRange = filterKey === 'custom'
    ? { start: customStart, end: customEnd }
    : buildDateRange(filterKey);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getFinancialReportSummary(dateRange.start, dateRange.end);
      if (res.error) {
        setError(res.error);
      } else {
        setSummary(res.data);
      }
    } catch {
      setError('Rapor yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [dateRange.start, dateRange.end]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Export helpers
  const handleExportPdf = async () => {
    if (!summary) return;
    setExportingPdf(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const now = new Date().toLocaleDateString('tr-TR');

      doc.setFillColor(245, 245, 255);
      doc.rect(0, 0, pageWidth, 30, 'F');
      doc.setFontSize(18);
      doc.setTextColor(30, 30, 80);
      doc.text('CIRO & RAPOR OZETI', pageWidth / 2, 14, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 130);
      doc.text(`Rapor Tarihi: ${now}  |  Donem: ${dateRange.start} - ${dateRange.end}`, pageWidth / 2, 22, { align: 'center' });

      (doc as any).autoTable({
        startY: 38,
        head: [['Metrik', 'Deger']],
        body: [
          ['Toplam Ciro', formatTRY(summary.total_revenue)],
          ['Satis Cirosu', formatTRY(summary.sales_revenue)],
          ['Bagimsiz Senet Cirosu', formatTRY(summary.independent_note_revenue)],
          ['Satilan Urunlerin Maliyeti', formatTRY(summary.total_cost)],
          ['Brüt Kar (maliyeti bilinen satislardan)', formatTRY(summary.gross_profit)],
          ['Kesin Brüt Kar', formatTRY(summary.certain_gross_profit ?? summary.gross_profit)],
          ['Toplam Gider', formatTRY(summary.total_expenses)],
          ['Net Kar', formatTRY(summary.net_profit)],
          ['Tahsil Edilen', formatTRY(summary.collected_amount)],
          ['Bekleyen Alacak', formatTRY(summary.outstanding_amount)],
          ['Maliyeti Bilinmeyen Ciro', formatTRY(summary.unknown_cost_revenue)],
          ['Toplam Satis', String(summary.sales_count)],
          ['Maliyeti Bilinen Satis', String(summary.known_cost_sales_count || 0)],
          ['Maliyeti Eksik Satis', String(summary.unknown_cost_sales_count || 0)],
        ],
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [99, 102, 241], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 248, 255] },
        margin: { left: 14, right: 14 },
      });

      doc.save(`ciro-rapor-${now.replace(/\./g, '-')}.pdf`);
    } catch (err) {
      console.error('PDF export hatası:', err);
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = () => {
    if (!summary) return;
    setExportingExcel(true);
    try {
      const wb = XLSX.utils.book_new();
      const now = new Date().toLocaleDateString('tr-TR');

      const summaryData = [
        ['Metrik', 'Değer'],
        ['Toplam Ciro', summary.total_revenue],
        ['Satış Cirosu', summary.sales_revenue],
        ['Bağımsız Senet Cirosu', summary.independent_note_revenue],
        ['Satılan Ürünlerin Maliyeti', summary.total_cost],
        ['Brüt Kâr', summary.gross_profit],
        ['Kesin Brüt Kâr', summary.certain_gross_profit ?? summary.gross_profit],
        ['Toplam Gider', summary.total_expenses],
        ['Net Kâr', summary.net_profit],
        ['Tahsil Edilen', summary.collected_amount],
        ['Bekleyen Alacak', summary.outstanding_amount],
        ['Maliyeti Bilinmeyen Ciro', summary.unknown_cost_revenue],
        ['Kârı Hesaplanabilen Ciro', summary.known_cost_revenue],
        ['Toplam Satış Sayısı', summary.sales_count],
        ['Maliyeti Bilinen Satış', summary.known_cost_sales_count || 0],
        ['Maliyeti Eksik Satış', summary.unknown_cost_sales_count || 0],
        ['Rapor Tarihi', now],
      ];
      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Özet');
      XLSX.writeFile(wb, `ciro-rapor-${now.replace(/\./g, '-')}.xlsx`);
    } catch (err) {
      console.error('Excel export hatası:', err);
    } finally {
      setExportingExcel(false);
    }
  };

  // ── Render ──
  if (loading && !summary) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p className="text-sm text-foreground-400">Rapor hesaplanıyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-2xl text-red-400"></i>
        </div>
        <p className="text-sm text-red-500 mb-2">{error}</p>
        <button onClick={loadReport} className="btn-primary cursor-pointer whitespace-nowrap">Yeniden Dene</button>
      </div>
    );
  }

  const s = summary!;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Ciro &amp; Raporlar</h2>
          <p className="text-sm text-foreground-400 mt-0.5">
            {dateRange.start} — {dateRange.end}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Date filter tabs */}
          <div className="flex items-center bg-background-100 rounded-full p-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => f.key !== 'custom' ? setFilterKey(f.key) : setFilterKey('custom')}
                className={`text-xs px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                  filterKey === f.key
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'text-foreground-600 hover:text-foreground-800'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {/* Export buttons */}
          <button
            onClick={handleExportPdf}
            disabled={exportingPdf}
            className="btn-outline flex items-center gap-1.5 px-3 py-2 text-xs cursor-pointer disabled:opacity-60 whitespace-nowrap"
          >
            {exportingPdf ? (
              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <i className="ri-file-pdf-line text-red-500"></i>
            )}
            <span className="hidden sm:inline">PDF</span>
          </button>
          <button
            onClick={handleExportExcel}
            disabled={exportingExcel}
            className="btn-outline flex items-center gap-1.5 px-3 py-2 text-xs cursor-pointer disabled:opacity-60 whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line text-emerald-600"></i>
            <span className="hidden sm:inline">Excel</span>
          </button>
        </div>
      </div>

      {/* Custom date range */}
      {filterKey === 'custom' && (
        <div className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-xl p-3">
          <span className="text-xs text-foreground-500">Tarih aralığı:</span>
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="text-sm border border-background-200 rounded-lg px-3 py-1.5 bg-transparent text-foreground-800"
          />
          <span className="text-xs text-foreground-400">—</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="text-sm border border-background-200 rounded-lg px-3 py-1.5 bg-transparent text-foreground-800"
          />
        </div>
      )}

      {/* Summary Cards — Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div
          className="bg-background-50 rounded-xl border border-background-200 p-4 cursor-pointer hover:border-primary-300 transition-colors"
          onClick={() => setDetailCardType('revenue')}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-foreground-400">Toplam Ciro</p>
            <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-primary-600"></i>
            </span>
          </div>
          <p className="text-2xl font-bold text-foreground-900 font-heading">{formatTRY(s.total_revenue)}</p>
          <p className="text-[11px] text-foreground-400 mt-1">
            Satış: {formatTRY(s.sales_revenue)} {s.independent_note_revenue > 0 ? `+ Senet: ${formatTRY(s.independent_note_revenue)}` : ''}
          </p>
          <div className="flex items-center gap-1 mt-2 text-xs text-primary-500 font-medium">
            <i className="ri-zoom-in-line"></i>
            <span>Detayları Gör</span>
          </div>
        </div>

        <div
          className="bg-background-50 rounded-xl border border-background-200 p-4 cursor-pointer hover:border-accent-300 transition-colors"
          onClick={() => setDetailCardType('gross_profit')}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-foreground-400">Brüt Kâr</p>
            <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
              <i className="ri-line-chart-line text-accent-600"></i>
            </span>
          </div>
          <p className={`text-2xl font-bold font-heading ${s.gross_profit >= 0 ? 'text-accent-600' : 'text-red-500'}`}>
            {formatTRY(s.gross_profit)}
          </p>
          <p className="text-[11px] text-foreground-400 mt-1">
            Ciro - Ürün Maliyeti
          </p>
          <div className="flex items-center gap-1 mt-2 text-xs text-primary-500 font-medium">
            <i className="ri-zoom-in-line"></i>
            <span>Detayları Gör</span>
          </div>
        </div>

        <div
          className="bg-background-50 rounded-xl border border-background-200 p-4 cursor-pointer hover:border-accent-300 transition-colors"
          onClick={() => setShowProfitModal(true)}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-foreground-400">Net Kâr</p>
            <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <i className="ri-funds-line text-emerald-600"></i>
            </span>
          </div>
          <p className={`text-2xl font-bold font-heading ${s.net_profit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {formatTRY(s.net_profit)}
          </p>
          <p className="text-[11px] text-foreground-400 mt-1">
            Brüt Kâr - Gider ({formatTRY(s.total_expenses)})
          </p>
          <div className="flex items-center gap-1 mt-2 text-xs text-primary-500 font-medium">
            <i className="ri-zoom-in-line"></i>
            <span>Detayları Gör</span>
          </div>
        </div>

        <div
          className="bg-background-50 rounded-xl border border-background-200 p-4 cursor-pointer hover:border-amber-300 transition-colors"
          onClick={() => setDetailCardType('collection')}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-foreground-400">Tahsilat / Alacak</p>
            <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <i className="ri-bank-card-line text-amber-600"></i>
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold text-emerald-600 font-heading">{formatTRY(s.collected_amount)}</span>
            <span className="text-xs text-foreground-400">tahsil</span>
          </div>
          <p className="text-lg font-bold text-amber-600 font-heading">{formatTRY(s.outstanding_amount)}</p>
          <p className="text-[11px] text-foreground-400">bekleyen alacak</p>
          <div className="flex items-center gap-1 mt-2 text-xs text-primary-500 font-medium">
            <i className="ri-zoom-in-line"></i>
            <span>Detayları Gör</span>
          </div>
        </div>
      </div>

      {/* Summary Cards — Row 2 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div
          className="bg-background-50 rounded-xl border border-background-200 p-4 cursor-pointer hover:border-primary-300 transition-colors"
          onClick={() => setDetailCardType('sales_count')}
        >
          <p className="text-xs text-foreground-400 mb-1">Toplam Satış</p>
          <p className="text-xl font-bold text-foreground-900 font-heading">{s.sales_count.toLocaleString('tr-TR')} adet</p>
          <p className="text-[10px] text-foreground-400 mt-0.5">
            Maliyeti bilinen: {(s.known_cost_sales_count || 0).toLocaleString('tr-TR')} | Eksik: {(s.unknown_cost_sales_count || 0).toLocaleString('tr-TR')}
          </p>
          <div className="flex items-center gap-1 mt-2 text-xs text-primary-500 font-medium">
            <i className="ri-zoom-in-line"></i>
            <span>Listeyi Gör</span>
          </div>
        </div>
        <div
          className="bg-background-50 rounded-xl border border-background-200 p-4 cursor-pointer hover:border-red-300 transition-colors"
          onClick={() => setDetailCardType('total_cost')}
        >
          <p className="text-xs text-foreground-400 mb-1">Satılan Ürün Maliyeti</p>
          <p className="text-xl font-bold text-foreground-900 font-heading">{formatTRY(s.total_cost)}</p>
          <div className="flex items-center gap-1 mt-2 text-xs text-primary-500 font-medium">
            <i className="ri-zoom-in-line"></i>
            <span>Detayları Gör</span>
          </div>
        </div>
        <div
          className="bg-background-50 rounded-xl border border-background-200 p-4 cursor-pointer hover:border-red-300 transition-colors"
          onClick={() => setDetailCardType('total_expenses')}
        >
          <p className="text-xs text-foreground-400 mb-1">Toplam Gider</p>
          <p className="text-xl font-bold text-red-500 font-heading">{formatTRY(s.total_expenses)}</p>
          <div className="flex items-center gap-1 mt-2 text-xs text-primary-500 font-medium">
            <i className="ri-zoom-in-line"></i>
            <span>Detayları Gör</span>
          </div>
        </div>
        <div
          className="bg-background-50 rounded-xl border border-background-200 p-4 cursor-pointer hover:border-accent-300 transition-colors"
          onClick={() => setDetailCardType('known_profit')}
        >
          <p className="text-xs text-foreground-400 mb-1">Kesin Brüt Kâr</p>
          <p className={`text-xl font-bold font-heading ${(s.certain_gross_profit ?? s.gross_profit) >= 0 ? 'text-accent-600' : 'text-red-500'}`}>{formatTRY(s.certain_gross_profit ?? s.gross_profit)}</p>
          <p className="text-[10px] text-foreground-400">Sadece maliyeti bilinen {s.known_cost_sales_count || 0} satıştan</p>
          <div className="flex items-center gap-1 mt-1 text-xs text-primary-500 font-medium">
            <i className="ri-zoom-in-line"></i>
            <span>Detayları Gör</span>
          </div>
        </div>
        <div
          className="bg-background-50 rounded-xl border border-background-200 p-4 cursor-pointer hover:border-amber-300 transition-colors"
          onClick={() => setDetailCardType('unknown_cost')}
        >
          <p className="text-xs text-foreground-400 mb-1">Maliyeti Bilinmeyen Ciro</p>
          <p className="text-xl font-bold text-foreground-500 font-heading">{formatTRY(s.unknown_cost_revenue)}</p>
          <p className="text-[10px] text-foreground-400">{s.unknown_cost_revenue > 0 ? 'Eski senet / bağımsız borç' : 'Tüm maliyetler biliniyor'}</p>
          <div className="flex items-center gap-1 mt-1 text-xs text-primary-500 font-medium">
            <i className="ri-zoom-in-line"></i>
            <span>Detayları Gör</span>
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div
          className="bg-background-50 rounded-xl border border-background-200 p-5 cursor-pointer hover:border-primary-300 transition-colors"
          onClick={() => setDetailCardType('revenue')}
        >
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground-800 font-heading">Ciro Dağılımı</h4>
            <div className="flex items-center gap-1 text-xs text-primary-500 font-medium">
              <i className="ri-zoom-in-line"></i>
              <span>Detayları Gör</span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground-600">Satış Cirosu</span>
              <span className="text-sm font-semibold text-foreground-800">{formatTRY(s.sales_revenue)}</span>
            </div>
            <div className="h-2 bg-background-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full"
                style={{ width: `${s.total_revenue > 0 ? (s.sales_revenue / s.total_revenue) * 100 : 0}%` }}
              ></div>
            </div>
            {s.independent_note_revenue > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-600">Bağımsız Senet / Eski Borç</span>
                  <span className="text-sm font-semibold text-foreground-800">{formatTRY(s.independent_note_revenue)}</span>
                </div>
                <div className="h-2 bg-background-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent-500 rounded-full"
                    style={{ width: `${s.total_revenue > 0 ? (s.independent_note_revenue / s.total_revenue) * 100 : 0}%` }}
                  ></div>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-6 mt-5 pt-4 border-t border-background-100">
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-primary-500"></span><span className="text-xs text-foreground-500">Satış</span></div>
            {s.independent_note_revenue > 0 && (
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-accent-500"></span><span className="text-xs text-foreground-500">Senet/Devir</span></div>
            )}
          </div>
        </div>

        <div
          className="bg-background-50 rounded-xl border border-background-200 p-5 cursor-pointer hover:border-accent-300 transition-colors"
          onClick={() => setShowProfitModal(true)}
        >
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground-800 font-heading">Kâr Özeti</h4>
            <div className="flex items-center gap-1 text-xs text-primary-500 font-medium">
              <i className="ri-zoom-in-line"></i>
              <span>Detayları Gör</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-background-100">
              <span className="text-sm text-foreground-600">Toplam Ciro</span>
              <span className="text-sm font-semibold text-foreground-800">{formatTRY(s.total_revenue)}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-background-100">
              <span className="text-sm text-foreground-600">Satılan Ürünlerin Maliyeti</span>
              <span className="text-sm font-semibold text-red-500">- {formatTRY(s.total_cost)}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-background-100">
              <span className="text-sm font-medium text-foreground-700">Brüt Kâr</span>
              <span className={`text-sm font-bold ${s.gross_profit >= 0 ? 'text-accent-600' : 'text-red-500'}`}>{formatTRY(s.gross_profit)}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-background-100">
              <span className="text-sm text-foreground-600">İşletme Giderleri</span>
              <span className="text-sm font-semibold text-red-500">- {formatTRY(s.total_expenses)}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-semibold text-foreground-800">Net Kâr</span>
              <span className={`text-base font-bold ${s.net_profit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatTRY(s.net_profit)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Explanation footer */}
      <div className="bg-background-50 rounded-xl border border-background-200 p-5">
        <h4 className="font-semibold text-foreground-800 font-heading mb-3">Hesaplama Yöntemi</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs text-foreground-500">
          <div>
            <p className="font-medium text-foreground-700 mb-1">Ciro</p>
            <p>Satış toplamları + satışa bağlı olmayan bağımsız senet/eski borç toplamları. Aynı borç iki kez sayılmaz.</p>
          </div>
          <div>
            <p className="font-medium text-foreground-700 mb-1">Brüt Kâr</p>
            <p>Satış anındaki gerçek satış fiyatı ile satış anında kaydedilen alış maliyeti farkı. Stok kartındaki güncel fiyat kullanılmaz.</p>
          </div>
          <div>
            <p className="font-medium text-foreground-700 mb-1">Net Kâr</p>
            <p>Brüt Kâr eksi işletme giderleri. Eski borç/devir senetlerinde maliyet bilinmiyorsa kâr hesabına "maliyet bilgisi eksik" olarak yansıtılır.</p>
          </div>
          <div>
            <p className="font-medium text-foreground-700 mb-1">Tahsilat ≠ Ciro</p>
            <p>Tahsilat alınan ödemedir, ciroyu artırmaz. Sadece bekleyen alacak ve tahsil edilen değerleri değiştirir.</p>
          </div>
        </div>
      </div>

      {/* Profit Details Modal */}
      <ProfitDetailsModal
        open={showProfitModal}
        onClose={() => setShowProfitModal(false)}
        startDate={dateRange.start}
        endDate={dateRange.end}
      />

      {/* Card Detail Modal */}
      <CardDetailModal
        open={detailCardType !== null}
        onClose={() => setDetailCardType(null)}
        cardType={detailCardType}
        summary={s}
        startDate={dateRange.start}
        endDate={dateRange.end}
      />
    </div>
  );
}